import React, { Component } from "react";
import { Nav } from "ui-kit-ck-consultant";
import FadeIn from "react-fade-in";
import moment from "moment";

import ShowOptionsNGRA from "../../../components/general/ShowOptionsNGRA-v2";
import LiveTracking from "../../../components/performances/live-tracking/LiveTracking";

import axiosConfig from "../../../utils/axiosConfig";
import filterStore from "../../../stores/filterStore";
import AuthContext from "../../../context/AuthContext";

// Until the first response tells us the ETL cadence, poll once a minute. Past
// that the interval follows dataSource.intervalMinutes: polling faster than the
// pipeline writes cannot surface anything newer, it just re-fetches an
// identical payload and advertises a freshness that does not exist.
const BOOTSTRAP_REFRESH_MS = 60 * 1000;

export default class LiveTrackingView extends Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    const saved = filterStore.getState().getPageState("liveTracking");
    this.state = saved || {
      data: null,
      isLoading: false,
      network: 0,
      group: 0,
      region: 0,
      agency: 0,
      dateStart: moment().format("YYYY-MM-DD"),
      dateEnd: moment().format("YYYY-MM-DD"),
      natureEvent: [],
      lastFetchAt: null,
      refreshMs: BOOTSTRAP_REFRESH_MS,
    };
  }

  componentDidMount() {
    this.fetchData();
    this.startPolling(BOOTSTRAP_REFRESH_MS);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    filterStore.getState().setPageState("liveTracking", this.state);
  }

  // Re-arm only when the cadence actually changes, so a steady stream of
  // responses doesn't keep resetting the timer and postponing the next fetch.
  startPolling = (ms) => {
    if (this.pollMs === ms) return;
    this.pollMs = ms;
    clearInterval(this.interval);
    this.interval = setInterval(() => this.fetchData({ silent: true }), ms);
    this.setState({ refreshMs: ms });
  };

  handleDataChange = (data) => {
    filterStore.getState().setPageFilters("liveTracking", data);
    this.setState(
      {
        network: data.network,
        group: data.group,
        region: data.region,
        agency: [...data.agencyMultiples],
        natureEvent: data.natureEvent,
      },
      this.fetchData
    );
  };

  // Background polls must not show the loading bar: it mounts/unmounts on every
  // tick, which shifts the layout and makes the page look like it is reloading
  // even when nothing changed.
  fetchData = ({ silent = false } = {}) => {
    if (!silent) this.setState({ isLoading: true });
    axiosConfig({
      url: "/performances/overall/get-live",
      data: {
        network: this.state.network,
        group: this.state.group,
        region: this.state.region,
        agency: this.state.agency,
        natureEvent: this.state.natureEvent,
      },
    })
      .then((res) => {
        if (!res.data.success) {
          this.setState({ isLoading: false, lastFetchAt: Date.now() });
          return;
        }
        // The ETL only moves every few minutes, so most polls return exactly
        // what we already display. Keeping the previous object reference lets
        // React skip the whole subtree instead of tearing down and remounting
        // every feed row.
        const next = res.data.data;
        const unchanged =
          this.state.data &&
          JSON.stringify(this.state.data) === JSON.stringify(next);
        this.setState({
          data: unchanged ? this.state.data : next,
          isLoading: false,
          lastFetchAt: Date.now(),
        });
        // Follow the pipeline's own cadence rather than a hardcoded one.
        const minutes = Number(next?.dataSource?.intervalMinutes);
        if (Number.isFinite(minutes) && minutes > 0) {
          this.startPolling(minutes * 60 * 1000);
        }
      })
      .catch(() => this.setState({ isLoading: false, lastFetchAt: Date.now() }));
  };

  render() {
    return (
      <FadeIn>
        <Nav title="Suivi Live" className="mb-10 mb-20-xl" />
        <ShowOptionsNGRA
          isChange={true}
          className="mb-10 mb-20-xl"
          onDataChange={this.handleDataChange}
          initialFilters={filterStore
            .getState()
            .getPageFilters("liveTracking")}
          isDateStart={false}
          isDateEnd={false}
          isGlobal={true}
          isAgencyMultiples={true}
          isNatureEvent={true}
          visibilityRole={this.props.visibilityRole}
          visibilityType={this.props.visibilityType}
          preloadRole={this.props.preloadRole}
          preloadType={this.props.preloadType}
          limitUserType={this.props.limitUserType}
          limitUserRole={this.props.limitUserRole}
        />
        <LiveTracking
          data={this.state.data}
          isLoading={this.state.isLoading}
          lastFetchAt={this.state.lastFetchAt}
          refreshMs={this.state.refreshMs}
          onRefresh={this.fetchData}
        />
      </FadeIn>
    );
  }
}
